import { renderAiDraftPlaceholders } from "@productlane/api/src/lib/openai/prompts/placeholders"
import { convertNewlinesToBr } from "@productlane/lib/src/html"

const HTML_TAG_RE = /<(p|br|div|span|strong|em|ul|ol|li|a)\b/i
const ESCAPED_HTML_TAG_RE = /&lt;(p|br|div|span|strong|em|ul|ol|li|a)\b/i

function unescapeHtmlEntities(value: string): string {
	return value
		.replaceAll("&lt;", "<")
		.replaceAll("&gt;", ">")
		.replaceAll("&quot;", '"')
		.replaceAll("&#39;", "'")
		.replaceAll("&amp;", "&")
}

// An empty paragraph is how the composer and email pipeline represent a blank
// line between paragraphs (see use-send-message's `<p></p>` -> `<br>` mapping).
function wrapPlainTextAsHtml(text: string): string {
	return text
		.trim()
		.split(/(?:[ \t]*\r?\n){2,}/)
		.map((para) => `<p>${convertNewlinesToBr(para)}</p>`)
		.join("<p></p>")
}

// A paragraph the model meant as a blank line: `<p></p>`, `<p><br></p>` from a
// Tiptap round-trip, `<p>&nbsp;</p>` from a mail client, with or without attributes.
const BLANK_PARAGRAPH_RE = /<p\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi

// Keep the blank line between adjacent paragraphs the model intended. Dropping
// existing blank paragraphs first keeps this idempotent for already-normalized HTML.
function insertBlankParagraphs(html: string): string {
	return html
		.replace(BLANK_PARAGRAPH_RE, "")
		.replace(/<\/p>\s*<p(\s|>)/gi, "</p><p></p><p$1")
}

// AI-produced "HTML" arrives in three shapes: real HTML, entity-escaped HTML
// (the model escapes tags in tool args), or plain prose. Normalize all to HTML.
export function normalizeAiHtml(value: string): string {
	let html = value
	if (!HTML_TAG_RE.test(html) && ESCAPED_HTML_TAG_RE.test(html)) {
		html = unescapeHtmlEntities(html)
	}
	return HTML_TAG_RE.test(html)
		? insertBlankParagraphs(html)
		: wrapPlainTextAsHtml(html)
}

// The composer normalizes on apply, so a composer preview has to show the same
// blank lines or it reads as a different draft than the one that lands.
export function previewAiHtml(
	target: "composer" | "article" | "changelog",
	html: string,
): string {
	return target === "composer" ? normalizeAiHtml(html) : html
}

/** First name a draft signs with: the live profile name, else the one in the login session. */
export function draftSignerFirstName(
	profileName: string | null | undefined,
	sessionName: string | null | undefined,
): string | null {
	const name = profileName?.trim() || sessionName?.trim()
	return name ? name.split(/\s+/)[0]! : null
}

export function renderAiDraftAsHtml(
	rawDraft: string | null | undefined,
	loggedInUserName: string | null,
): string {
	if (!rawDraft) return ""
	return normalizeAiHtml(
		renderAiDraftPlaceholders(rawDraft, { loggedInUserName }),
	)
}
